// TransportPanel — assign a route (and pickup order) to the students whose
// families actually opted in to the bus. Most students in a school don't
// ride the bus, so this used to live inline on every Add Student form —
// now it's its own step, done after admission, only for the ones who need it.
import { useEffect, useMemo, useState } from 'react';
import { Bus, Receipt, X } from 'lucide-react';
import { getStudents, getRoutes, getClasses, updateStudent, getStudentServices, setStudentService, addFeeHead } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { inputCls, Msg, errText } from '../ui/Form';
import { inr } from '../ui/format';

// The PUT /students/:id endpoint takes the whole record, not a partial patch
// — building this from the row already returned by getStudents() keeps
// every other field (name, class, guardian, admission no…) untouched while
// only route_id/stop_order actually change.
function fullPayload(student, overrides) {
  return {
    name: student.name,
    class: student.class,
    class_id: student.class_id || null,
    roll_number: student.roll_number || null,
    guardian_id: student.guardian_id || null,
    admission_no: student.admission_no || null,
    date_of_birth: student.date_of_birth || null,
    gender: student.gender || null,
    father_name: student.father_name || null,
    mother_name: student.mother_name || null,
    address: student.address || null,
    blood_group: student.blood_group || null,
    category: student.category || null,
    date_of_admission: student.date_of_admission || null,
    route_id: null,
    stop_order: null,
    ...overrides,
  };
}

export default function TransportPanel({ onOpenStudent }) {
  const [students, setStudents] = useState([]);
  const [routes,   setRoutes]   = useState([]);
  const [classes,  setClasses]  = useState([]);
  const [routeId,  setRouteId]  = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]); // ids, in the order checked — becomes pickup order
  const [msg,      setMsg]      = useState('');
  const [busy,     setBusy]     = useState(false);
  const [feeFor,   setFeeFor]   = useState(null);   // student id whose fee editor is open
  const [feeState, setFeeState] = useState(null);   // { loading, head, amount } | null

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [s, r, c] = await Promise.all([getStudents(), getRoutes(), getClasses()]);
      setStudents(s); setRoutes(r); setClasses(c);
      if (r.length && !routeId) setRouteId(String(r[0].id));
    } catch { setMsg('Failed to load'); }
  }

  const assigned = useMemo(() =>
    students.filter(s => String(s.route_id) === routeId).sort((a, b) => (a.stop_order || 0) - (b.stop_order || 0)),
    [students, routeId]);

  const unassigned = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter(s => !s.route_id
      && (!classFilter || String(s.class_id) === classFilter)
      && (!q || [s.name, s.roll_number, s.admission_no, s.guardian_name, s.guardian_phone]
        .some(v => v && String(v).toLowerCase().includes(q))));
  }, [students, classFilter, search]);

  function toggle(id) {
    setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  }

  async function assignSelected() {
    if (!routeId || !selected.length) return;
    setBusy(true); setMsg('');
    const startOrder = assigned.length;
    const failures = [];
    for (let i = 0; i < selected.length; i++) {
      const student = students.find(s => s.id === selected[i]);
      try {
        await updateStudent(student.id, fullPayload(student, { route_id: +routeId, stop_order: startOrder + i + 1 }));
      } catch (err) {
        failures.push(`${student.name}: ${err.response?.data?.error || err.message}`);
      }
    }
    setSelected([]);
    setMsg(failures.length ? 'Error: ' + failures.join(' · ') : `Assigned ${selected.length - failures.length} student${selected.length - failures.length === 1 ? '' : 's'} ✅`);
    setBusy(false);
    load();
  }

  async function unassign(student) {
    setBusy(true); setMsg('');
    try {
      await updateStudent(student.id, fullPayload(student, { route_id: null, stop_order: null }));
      load();
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setBusy(false);
    }
  }

  async function openFee(student) {
    if (feeFor === student.id) { setFeeFor(null); setFeeState(null); return; }
    setFeeFor(student.id);
    setFeeState({ loading: true, head: null, amount: '' });
    try {
      const services = await getStudentServices(student.id);
      const head = services.find(s => /transport/i.test(s.head_name)) || null;
      setFeeState({ loading: false, head, amount: head ? String(head.amount) : '' });
    } catch {
      setFeeState({ loading: false, head: null, amount: '', error: true });
    }
  }

  async function saveFee(student, active) {
    if (!feeState?.amount && active) return; // need an amount to opt in (not required when just turning off)
    setBusy(true); setMsg('');
    try {
      // No "Transport" head yet anywhere in the school — create it (Optional,
      // Monthly, all classes) the first time the transport team sets a fee,
      // instead of sending them to Fee Structure first.
      let head = feeState.head;
      if (!head) {
        const created = await addFeeHead({
          head_name: 'Transport', amount: Number(feeState.amount), frequency: 'monthly', optional: true, class_id: null,
        });
        head = { head_id: created.id };
      }
      await setStudentService(student.id, head.head_id, {
        active, amount_override: feeState.amount !== '' ? Number(feeState.amount) : null,
      });
      setMsg(`Transport fee ${active ? 'saved' : 'turned off'} for ${student.name} ✅`);
      setFeeFor(null); setFeeState(null);
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-1">Transport</h2>
      <p className="text-sm text-ink-soft mb-4">
        Assign a route to students whose families opted in to the bus — everyone else just stays off a route.
        Pickup point is each student's guardian's own pinned home location, in the order you assign them here.
      </p>

      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Route
          <select className={inputCls} value={routeId} onChange={e => setRouteId(e.target.value)}>
            {!routes.length && <option value="">No routes yet</option>}
            {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
      </Card>

      {msg && <Msg msg={msg} className="mb-4" />}

      <div className="grid grid-cols-[1fr_1fr] gap-5 items-start max-[900px]:grid-cols-1">
        <Card>
          <CardHeader title={`On this route (${assigned.length})`} />
          {!assigned.length && <p className="px-5 py-6 text-sm text-ink-soft">No students assigned to this route yet.</p>}
          {assigned.map(s => (
            <div key={s.id} className="border-t border-line-soft first:border-t-0">
              <div className="flex items-center gap-3 px-5 py-2.5">
                <span className="text-xs font-bold text-ink-soft w-5 tabular-nums">{s.stop_order}</span>
                <button onClick={() => onOpenStudent(s.id)} className="text-sm font-semibold text-transit-deep hover:underline text-left flex-1 min-w-0 truncate">
                  {s.name}
                </button>
                {!s.stop_id && <Badge tone="caution">Pickup pending</Badge>}
                <span className="text-xs text-ink-soft">{s.class_name || '—'}</span>
                <button onClick={() => openFee(s)} disabled={busy} title="Set transport fee" className={`p-1 ${feeFor === s.id ? 'text-transit-deep' : 'text-ink-soft hover:text-transit-deep'}`}>
                  <Receipt size={14} />
                </button>
                <button onClick={() => unassign(s)} disabled={busy} title="Remove from this route" className="text-ink-soft hover:text-critical p-1">
                  <X size={14} />
                </button>
              </div>
              {feeFor === s.id && (
                <div className="px-5 pb-3 -mt-1">
                  {feeState?.loading ? (
                    <p className="text-xs text-ink-soft">Loading…</p>
                  ) : feeState?.error ? (
                    <p className="text-xs text-critical">Couldn't load fee services for {s.name}.</p>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="flex items-center gap-1.5 text-xs font-bold text-ink-soft">₹/month
                        <input type="number" min="0" step="any" className={`${inputCls} w-24 !py-1`}
                          value={feeState.amount} onChange={e => setFeeState({ ...feeState, amount: e.target.value })} />
                      </label>
                      <Button className="!px-2.5 !py-1 !text-xs" disabled={busy || !feeState.amount} onClick={() => saveFee(s, true)}>Save</Button>
                      {feeState.head?.active && (
                        <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" disabled={busy} onClick={() => saveFee(s, false)}>Turn off</Button>
                      )}
                      {feeState.head?.active ? (
                        <span className="text-xs text-ink-soft">Currently {inr(feeState.head.amount)}/mo — this adds to the student's monthly total and shows as "Transport" on their receipt.</span>
                      ) : (
                        <span className="text-xs text-ink-soft">No "Transport" fee head yet — saving here creates one automatically.</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>

        <div>
          <Card>
            <CardHeader title={`Not on transport (${unassigned.length})`} action={
              <div className="flex gap-2">
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, roll, guardian…" aria-label="Search students"
                  className="border border-line rounded-s px-2 py-1.5 text-xs bg-paper-raised text-ink w-44 focus:outline-none focus:ring-2 focus:ring-transit" />
                <select value={classFilter} onChange={e => setClassFilter(e.target.value)} aria-label="Filter by class"
                  className="border border-line rounded-s px-2 py-1.5 text-xs bg-paper-raised text-ink">
                  <option value="">All classes</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            } />
            {!unassigned.length && <p className="px-5 py-6 text-sm text-ink-soft">Every student in this list is already on a route.</p>}
            {unassigned.map(s => (
              <div key={s.id} className="flex items-center gap-3 px-5 py-2.5 border-t border-line-soft first:border-t-0">
                <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} aria-label={`Select ${s.name}`}
                  className="w-4 h-4 accent-[var(--transit)]" disabled={!s.guardian_id} />
                <button onClick={() => onOpenStudent(s.id)} className="text-sm font-semibold text-transit-deep hover:underline text-left flex-1 min-w-0 truncate">
                  {s.name}
                </button>
                <span className="text-xs text-ink-soft">{s.class_name || '—'}</span>
                {!s.guardian_id && <span className="text-[11px] font-bold text-caution">No guardian</span>}
              </div>
            ))}
          </Card>
          <Button className="mt-3 flex items-center gap-1.5" disabled={!selected.length || !routeId || busy} onClick={assignSelected}>
            <Bus size={14} /> Assign {selected.length || ''} to this route
          </Button>
          <p className="text-xs text-ink-soft mt-2">Ticked in the order you want them picked up — the first one checked becomes the first stop.</p>
          <p className="text-xs text-ink-soft mt-2">
            If a guardian hasn't pinned their home location yet, the route is still assigned ("Pickup pending") — their pickup point fills in automatically once they pin it.
          </p>
        </div>
      </div>
    </div>
  );
}
